import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchGroupsAsync } from '../store/slices/groupsSlice';
import { fetchVacationTypesAsync } from '../store/slices/studentsSlice';
import { createLessonAsync, updateLessonAsync } from '../store/slices/lessonsSlice';
import { fetchGroupGridAsync, clearJournal } from '../store/slices/journalSlice';
import type { GridStudent } from '../store/slices/journalSlice';
import type { JournalColumn } from '../utils/journalCell';
import { buildColumns, resolveCellState } from '../utils/journalCell';
import { formatDateEuropean } from '../utils/dateUtils';
import Select from './Select';
import type { SelectOption } from './AsyncSearchSelect';
import JournalGrid from './JournalGrid';
import JournalCellMenu from './JournalCellMenu';
import type { JournalMenuItem } from './JournalCellMenu';
import FormModal from './FormModal';
import SubscriptionForm from './SubscriptionForm';
import StudentVacationFormBody from './StudentVacationFormBody';
import ConfirmationModal from './ConfirmationModal';
import LoadingSpinner from './LoadingSpinner';
import './GroupForm.css';
import './Journal.css';

type MenuTarget =
  | { kind: 'cell'; student: GridStudent; column: JournalColumn }
  | { kind: 'header'; column: JournalColumn };

type ModalTarget =
  | { type: 'subscription'; student: GridStudent }
  | { type: 'sick' | 'vacation'; student: GridStudent; date: string };

/** Last opened group survives page reloads — the teacher returns where she left. */
const LAST_GROUP_STORAGE_KEY = 'journal:lastGroupId';

const Journal: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { groups } = useAppSelector((state) => state.groups);
  const { vacationTypes } = useAppSelector((state) => state.students);
  const { grid, loading, refreshing, error } = useAppSelector((state) => state.journal);

  const groupIdParam = searchParams.get('group');
  const groupId = groupIdParam ? parseInt(groupIdParam, 10) : null;

  const [showFormer, setShowFormer] = useState(false);
  const [menu, setMenu] = useState<{ target: MenuTarget; position: { top: number; left: number } } | null>(null);
  const [modal, setModal] = useState<ModalTarget | null>(null);
  const [cancelTarget, setCancelTarget] = useState<{ lessonId: number; date: string; toCanceled: boolean } | null>(null);
  const [topicDraft, setTopicDraft] = useState('');
  const [pendingCells, setPendingCells] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // ── Data loading ──────────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(fetchGroupsAsync({ page: 1, limit: 100 }));
    dispatch(fetchVacationTypesAsync());
  }, [dispatch]);

  // No ?group in the URL → restore the last opened group from localStorage.
  useEffect(() => {
    if (!groupIdParam) {
      const saved = localStorage.getItem(LAST_GROUP_STORAGE_KEY);
      if (saved) setSearchParams({ group: saved }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (groupId) {
      localStorage.setItem(LAST_GROUP_STORAGE_KEY, groupId.toString());
      dispatch(fetchGroupGridAsync({ groupId }));
    } else {
      dispatch(clearJournal());
    }
  }, [groupId, dispatch]);

  const refetchSilent = useCallback(() => {
    if (groupId) dispatch(fetchGroupGridAsync({ groupId, silent: true }));
  }, [groupId, dispatch]);

  // Scroll so today's column is in view once a grid loads
  const scrolledForGroup = useRef<number | null>(null);
  useEffect(() => {
    if (!grid || !scrollRef.current) return;
    if (scrolledForGroup.current === grid.group.id) return;
    scrolledForGroup.current = grid.group.id;
    const container = scrollRef.current;
    const target = container.querySelector<HTMLElement>(
      `th[data-date="${grid.range.today}"]`
    ) || Array.from(container.querySelectorAll<HTMLElement>('th[data-date]'))
      .find((el) => (el.dataset.date || '') >= grid.range.today);
    if (target) {
      container.scrollLeft = Math.max(0, target.offsetLeft - container.clientWidth * 0.55);
    } else {
      container.scrollLeft = container.scrollWidth;
    }
  }, [grid]);

  // ── Derived data ──────────────────────────────────────────────────────────

  const columns = useMemo(() => {
    if (!grid) return [];
    const end = grid.group.endDate;
    const endInRange = end && end >= grid.range.from && end <= grid.range.to ? end : null;
    return buildColumns(grid.lessons, grid.ghostDates, grid.teacherVacations, endInRange);
  }, [grid]);

  const students = useMemo(() => {
    if (!grid) return [];
    return showFormer ? grid.students : grid.students.filter((s) => !s.isFormer);
  }, [grid, showFormer]);

  const groupOptions = useMemo(
    () =>
      groups.map((g) => ({
        value: g.id,
        label: `${g.name || `${g.level.toUpperCase()} Group`} (${g.level.toUpperCase()})`,
      })),
    [groups]
  );
  const selectedGroupOption = groupOptions.find((o) => o.value === groupId) ?? null;

  const illnessType = vacationTypes.find((t) => t.isIllness);
  const regularVacationType = vacationTypes.find((t) => !t.isIllness && !t.isFreeze);

  // ── Mutations (existing thunks + silent grid refetch) ─────────────────────

  const markPending = (key: string, on: boolean) =>
    setPendingCells((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const toggleAbsence = (student: GridStudent, column: JournalColumn) => {
    if (column.kind !== 'lesson') return;
    const lesson = column.lesson;
    const key = `${column.date}:${student.id}`;
    const next = lesson.absentStudentIds.includes(student.id)
      ? lesson.absentStudentIds.filter((id) => id !== student.id)
      : [...lesson.absentStudentIds, student.id];
    setMenu(null);
    markPending(key, true);
    dispatch(updateLessonAsync({ id: lesson.id, data: { absentStudentIds: next } }))
      .unwrap()
      .then(() => setActionError(null))
      .catch((err) => setActionError(typeof err === 'string' ? err : 'Failed to update absence'))
      .finally(() => {
        markPending(key, false);
        refetchSilent();
      });
  };

  const createLesson = (date: string, canceled = false) => {
    if (!groupId) return;
    setMenu(null);
    dispatch(createLessonAsync({ groupId, date, ...(canceled ? { canceled: true } : {}) }))
      .unwrap()
      .then(() => setActionError(null))
      .catch((err) => setActionError(typeof err === 'string' ? err : 'Failed to create lesson'))
      .finally(refetchSilent);
  };

  const saveTopic = (lessonId: number) => {
    setMenu(null);
    dispatch(updateLessonAsync({ id: lessonId, data: { topic: topicDraft.trim() || undefined } }))
      .unwrap()
      .then(() => setActionError(null))
      .catch((err) => setActionError(typeof err === 'string' ? err : 'Failed to update topic'))
      .finally(refetchSilent);
  };

  const confirmCancel = () => {
    if (!cancelTarget) return;
    const { lessonId, toCanceled } = cancelTarget;
    setCancelTarget(null);
    dispatch(updateLessonAsync({ id: lessonId, data: { canceled: toCanceled } }))
      .unwrap()
      .then(() => setActionError(null))
      .catch((err) => setActionError(typeof err === 'string' ? err : 'Failed to update lesson'))
      .finally(refetchSilent);
  };

  // ── Menu construction ─────────────────────────────────────────────────────

  const openCellMenu = (student: GridStudent, column: JournalColumn, rect: DOMRect) => {
    setMenu({
      target: { kind: 'cell', student, column },
      position: { top: rect.bottom + 4, left: rect.left },
    });
  };

  const openHeaderMenu = (column: JournalColumn, rect: DOMRect) => {
    if (column.kind === 'teacherVacation' || column.kind === 'groupEnd') return;
    if (column.kind === 'lesson') setTopicDraft(column.lesson.topic || '');
    setMenu({
      target: { kind: 'header', column },
      position: { top: rect.bottom + 4, left: rect.left },
    });
  };

  const menuContent = useMemo((): { title: string; items: JournalMenuItem[]; extra?: React.ReactNode } | null => {
    if (!menu) return null;
    const { target } = menu;

    if (target.kind === 'cell') {
      const { student, column } = target;
      const name = `${student.firstName} ${student.lastName}`.trim();
      const title = `${name} · ${formatDateEuropean(column.date)}`;
      const items: JournalMenuItem[] = [];

      if (column.kind === 'ghost') {
        items.push({ key: 'create', label: 'Create lesson', onClick: () => createLesson(column.date) });
        items.push({
          key: 'cancel',
          label: 'Cancel lesson',
          danger: true,
          onClick: () => createLesson(column.date, true),
        });
      } else if (column.kind === 'lesson') {
        const { state } = resolveCellState(student, column);
        if (!column.lesson.canceled && state !== 'frozen') {
          const isAbsent = column.lesson.absentStudentIds.includes(student.id);
          items.push({
            key: 'absence',
            label: isAbsent ? 'Mark present' : 'Mark absent',
            onClick: () => toggleAbsence(student, column),
          });
        }
      }

      items.push({
        key: 'subscription',
        label: 'New subscription…',
        onClick: () => {
          setMenu(null);
          setModal({ type: 'subscription', student });
        },
      });
      items.push({
        key: 'sick',
        label: 'Add sick leave…',
        onClick: () => {
          setMenu(null);
          setModal({ type: 'sick', student, date: target.column.date });
        },
      });
      items.push({
        key: 'vacation',
        label: 'Add vacation…',
        onClick: () => {
          setMenu(null);
          setModal({ type: 'vacation', student, date: target.column.date });
        },
      });

      return { title, items };
    }

    // Header menu
    const { column } = target;
    if (column.kind === 'ghost') {
      return {
        title: `${formatDateEuropean(column.date)} · planned`,
        items: [
          { key: 'create', label: 'Create lesson', onClick: () => createLesson(column.date) },
          {
            key: 'cancel',
            label: 'Cancel lesson',
            danger: true,
            onClick: () => createLesson(column.date, true),
          },
        ],
      };
    }
    if (column.kind === 'lesson') {
      const lesson = column.lesson;
      const items: JournalMenuItem[] = [
        {
          key: 'cancel',
          label: lesson.canceled ? 'Restore lesson' : 'Cancel lesson',
          danger: !lesson.canceled,
          onClick: () => {
            setMenu(null);
            setCancelTarget({ lessonId: lesson.id, date: lesson.date, toCanceled: !lesson.canceled });
          },
        },
        {
          key: 'open',
          label: 'Open in Lesson editor',
          onClick: () => navigate(`/lesson?date=${lesson.date}&group=${groupId}`),
        },
      ];
      const extra = !lesson.canceled ? (
        <div className="journal-menu__topic">
          <input
            type="text"
            className="form-input"
            placeholder="Lesson topic"
            value={topicDraft}
            onChange={(e) => setTopicDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveTopic(lesson.id);
            }}
          />
          <button
            type="button"
            className="btn-success journal-menu__topic-save"
            onClick={() => saveTopic(lesson.id)}
            disabled={(lesson.topic || '') === topicDraft.trim()}
          >
            Save
          </button>
        </div>
      ) : undefined;
      return {
        title: `${formatDateEuropean(lesson.date)}${lesson.canceled ? ' · canceled' : ''}`,
        items,
        extra,
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, topicDraft, groupId]);

  // ── Render ────────────────────────────────────────────────────────────────

  const closeModalAndRefetch = () => {
    setModal(null);
    refetchSilent();
  };

  return (
    <div className="journal-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Journal</h1>
          <p>Lessons, absences and subscriptions of a group at a glance</p>
        </div>
      </div>

      <div className="journal-body">
        <div className="filter-bar">
          <div className="journal-group-select">
            <Select<number, SelectOption<number>>
              value={selectedGroupOption}
              onChange={(option) => {
                setShowFormer(false);
                if (option) setSearchParams({ group: option.value.toString() });
                else setSearchParams({});
              }}
              options={groupOptions}
              placeholder="Choose a group…"
              isClearable={false}
              noOptionsMessage="No groups"
            />
          </div>
          <div className="journal-segments" role="group" aria-label="Students filter">
            <button
              type="button"
              className={`journal-segments__btn ${!showFormer ? 'is-active' : ''}`}
              onClick={() => setShowFormer(false)}
            >
              Active
            </button>
            <button
              type="button"
              className={`journal-segments__btn ${showFormer ? 'is-active' : ''}`}
              onClick={() => setShowFormer(true)}
            >
              All students
            </button>
          </div>
          {refreshing && <span className="journal-refreshing">Saving…</span>}
        </div>

        {(error || actionError) && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            {actionError || error}
            <button type="button" onClick={() => setActionError(null)} className="close-error">×</button>
          </div>
        )}

        {!groupId && (
          <div className="journal-empty">Choose a group to open its journal.</div>
        )}

        {groupId && loading && <LoadingSpinner />}

        {groupId && !loading && grid && (
          <>
            <div className="journal-legend">
              <span><i className="journal-legend__chip journal-legend__chip--attended">✓</i> attended</span>
              <span><i className="journal-legend__chip journal-legend__chip--absent">A</i> absent</span>
              <span><i className="journal-legend__chip journal-legend__chip--ill">S</i> sick</span>
              <span><i className="journal-legend__chip journal-legend__chip--vacation">V</i> vacation</span>
              <span><i className="journal-legend__chip journal-legend__chip--frozen">❄</i> frozen</span>
              <span><i className="journal-legend__chip journal-legend__chip--ghost" /> planned</span>
              <span><i className="journal-legend__chip journal-legend__chip--sub" /> subscription start</span>
              <span><i className="journal-legend__chip journal-legend__chip--canceled">✕</i> canceled</span>
            </div>
            <JournalGrid
              columns={columns}
              students={students}
              today={grid.range.today}
              pendingCells={pendingCells}
              onCellClick={openCellMenu}
              onHeaderClick={openHeaderMenu}
              scrollRef={scrollRef}
            />
          </>
        )}
      </div>

      {menu && menuContent && (
        <JournalCellMenu
          position={menu.position}
          title={menuContent.title}
          items={menuContent.items}
          extra={menuContent.extra}
          onClose={() => setMenu(null)}
        />
      )}

      {modal?.type === 'subscription' && (
        <FormModal isOpen title="New subscription" onClose={() => setModal(null)} wide>
          <SubscriptionForm
            prefillStudentId={modal.student.id}
            lockStudent
            onSuccess={closeModalAndRefetch}
            onCancel={() => setModal(null)}
          />
        </FormModal>
      )}

      {(modal?.type === 'sick' || modal?.type === 'vacation') && (
        <FormModal
          isOpen
          title={modal.type === 'sick' ? 'Add sick leave' : 'Add vacation'}
          onClose={() => setModal(null)}
          wide
        >
          <StudentVacationFormBody
            studentId={modal.student.id}
            lockStudent
            prefill={{
              dateFrom: modal.date,
              dateTo: modal.date,
              vacationTypeId: modal.type === 'sick' ? illnessType?.id : regularVacationType?.id,
            }}
            onSuccess={closeModalAndRefetch}
            onCancel={() => setModal(null)}
          />
        </FormModal>
      )}

      <ConfirmationModal
        isOpen={cancelTarget !== null}
        title={cancelTarget?.toCanceled ? 'Cancel lesson' : 'Restore lesson'}
        message={
          cancelTarget?.toCanceled
            ? `Cancel the lesson on ${cancelTarget ? formatDateEuropean(cancelTarget.date) : ''}? It will not consume subscription lessons and its absences will be cleared.`
            : `Restore the lesson on ${cancelTarget ? formatDateEuropean(cancelTarget.date) : ''}? It will consume subscription lessons again.`
        }
        onConfirm={confirmCancel}
        onCancel={() => setCancelTarget(null)}
        confirmText={cancelTarget?.toCanceled ? 'Cancel lesson' : 'Restore'}
        dangerous={cancelTarget?.toCanceled}
      />
    </div>
  );
};

export default Journal;
